import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { addStudentMemo, addStudentMemos, deleteStudentMemo, newMemoClientId } from '../../api/classRosterApi';
import { useToast } from '../../components/Toast';
import { trackClassRosterEvent } from '../../utils/eventTracker';
import {
  QUICK_TAGS,
  formatShortDay,
  quickMemoArea,
  quickMemoText,
  schoolYearRange,
  todayKey,
  yesterdayKey,
} from '../../utils/memoQuick';
import { MEMO_MAX_CHARS, restrictedMemoKinds, suggestMemoArea } from '../../utils/studentMemos';
import ClassSheet from './ClassSheet';

const finePointer = () => typeof window !== 'undefined' && typeof window.matchMedia === 'function'
  && window.matchMedia('(pointer: fine)').matches;

/** 오늘·어제·날짜 고르기. 명단 학년도 밖 날짜는 고를 수 없다. */
export const DateChips = ({ day, onChange, schoolYear }) => {
  const pickerRef = useRef(null);
  const [showInput, setShowInput] = useState(false);
  const today = todayKey();
  const yesterday = yesterdayKey();
  const range = schoolYear ? schoolYearRange(schoolYear) : null;
  const inRange = key => !range || (key >= range.min && key <= range.max);
  const max = range && range.max < today ? range.max : today;
  const custom = Boolean(day) && day !== today && day !== yesterday;

  const openPicker = () => {
    try {
      pickerRef.current.showPicker();
    } catch (error) {
      setShowInput(true);
    }
  };

  return (
    <div className="class-memo-dates" role="group" aria-label="날짜">
      {inRange(today) && (
        <button type="button" aria-pressed={day === today} className={day === today ? 'is-active' : ''} onClick={() => onChange(today)}>오늘</button>
      )}
      {inRange(yesterday) && (
        <button type="button" aria-pressed={day === yesterday} className={day === yesterday ? 'is-active' : ''} onClick={() => onChange(yesterday)}>어제</button>
      )}
      <button type="button" aria-pressed={custom} className={custom ? 'is-active' : ''} onClick={openPicker}>
        {custom ? formatShortDay(day) : '날짜 고르기'}
      </button>
      <input
        ref={pickerRef}
        type="date"
        aria-label="날짜 고르기"
        className={showInput ? 'class-memo-date-input' : 'class-memo-date-input is-hidden'}
        value={day || ''}
        min={range?.min}
        max={max}
        onChange={(event) => { if (event.target.value) onChange(event.target.value); }}
      />
    </div>
  );
};

/**
 * 학생 칸을 누르면 뜨는 메모 시트. 태그를 누르면 바로 남기고(2번 터치), 한 줄을 적고 Enter로도 남긴다.
 * targets가 여럿이면 같은 메모를 '함께한 활동'으로 한 번에 남긴다.
 */
const MemoSheet = ({ roster, targets, memoCount = 0, day, onDayChange, onClose, onDone, onSaved, onRemoved, onNext, studentPath }) => {
  const { showToast } = useToast();
  const [text, setText] = useState('');
  const [saved, setSaved] = useState([]);
  const lastRef = useRef({ key: '', at: 0 });
  const attemptsRef = useRef(new Map());
  const inputRef = useRef(null);
  const group = targets.length > 1;
  const single = targets[0];
  const targetKey = targets.map(item => item.sid).join(',');

  useEffect(() => {
    setText('');
    setSaved([]);
    if (finePointer()) inputRef.current?.focus();
  }, [targetKey]);

  const save = async (tag = '') => {
    const body = quickMemoText(tag, text);
    if (!body || !day) return;
    const key = `${targetKey}|${body}|${day}`;
    if (lastRef.current.key === key && Date.now() - lastRef.current.at < 1500) return;
    lastRef.current = { key, at: Date.now() };
    const area = quickMemoArea(tag, suggestMemoArea(text));
    const typed = text;
    setText('');
    // 같은 학생·날짜·내용을 다시 남기는 시도(실패 뒤 재시도)는 같은 client_id를 써서 서버에서 겹치지 않게 한다.
    const attemptKey = `${key}|${area}`;
    if (!attemptsRef.current.has(attemptKey)) attemptsRef.current.set(attemptKey, newMemoClientId());
    const clientId = attemptsRef.current.get(attemptKey);
    try {
      let memos;
      if (group) {
        const base = clientId.slice(0, 26);
        memos = await addStudentMemos(roster.id, targets.map((item, index) => ({
          sid: item.sid, text: body, area, date: day, clientId: `${base}${String(index).padStart(3, '0')}`,
        })), 'group');
      } else {
        memos = [await addStudentMemo(roster.id, single.sid, { text: body, area, date: day, clientId })];
      }
      attemptsRef.current.delete(attemptKey);
      onSaved(memos);
      if (!group) setSaved(prev => [memos[0], ...prev]);
      trackClassRosterEvent('class_memo_quick_saved', {
        roster_id: roster.id,
        mode: tag && typed.trim() ? 'tag_text' : tag ? 'tag' : 'text',
        scope: group ? 'group' : 'one',
        student_count: targets.length,
        memo_chars: body.length,
        has_area: Boolean(area),
        not_today: day !== todayKey(),
      });
      if (group) {
        showToast(`${targets.length}명에게 남겼어요`, 'success', 5000, { label: '되돌리기', onClick: () => undo(memos) });
        (onDone || onClose)();
      }
    } catch (error) {
      setText(current => current || typed);
      lastRef.current = { key: '', at: 0 };
      showToast(error.message || '메모를 남기지 못했어요. 다시 시도해 주세요.', 'error');
    }
  };

  // 한 명 시트는 토스트가 '다음 학생'을 가리지 않게 남긴 줄 옆에서 되돌린다.
  const undo = async (memos) => {
    try {
      await Promise.all(memos.map(memo => deleteStudentMemo(memo.id)));
      onRemoved(memos);
      setSaved(prev => prev.filter(item => item.id !== memos[0].id));
    } catch (error) {
      showToast(error.message || '되돌리지 못했어요. 다시 시도해 주세요.', 'error');
    }
  };

  const onKeyDown = (event) => {
    if (event.key !== 'Enter' || event.nativeEvent?.isComposing || event.keyCode === 229) return;
    event.preventDefault();
    save('');
  };

  const restricted = restrictedMemoKinds(text);
  const title = group ? `${targets.length}명에게 남기기` : `${single.number} ${single.name}`;
  const subtitle = group ? '함께한 활동으로 남겨요' : `메모 ${memoCount}개`;

  return (
    <ClassSheet
      title={title}
      subtitle={subtitle}
      onClose={onClose}
      testId="class-memo-sheet"
      footer={!group && (
        <>
          <Link className="class-sheet-link" to={studentPath(single)}>기록 전체 보기</Link>
          {onNext && <button type="button" className="classes-btn-secondary" onClick={onNext}>다음 학생 ›</button>}
        </>
      )}
    >
      <DateChips day={day} onChange={onDayChange} schoolYear={roster.school_year} />
      {!day && <p className="class-memo-warn">날짜를 골라 주세요</p>}
      <div className="class-memo-tags" role="group" aria-label="누르면 바로 남겨요">
        {QUICK_TAGS.map(tag => (
          <button key={tag.label} type="button" disabled={!day} onClick={() => save(tag.label)}>{tag.label}</button>
        ))}
      </div>
      <form className="class-memo-input" onSubmit={(event) => { event.preventDefault(); save(''); }}>
        <input
          ref={inputRef}
          type="text"
          aria-label="한 줄 메모"
          enterKeyHint="done"
          maxLength={MEMO_MAX_CHARS}
          value={text}
          placeholder={group ? '함께 한 일 한 줄' : '본 모습 한 줄 · 태그와 함께 남겨도 돼요'}
          onChange={event => setText(event.target.value)}
          onKeyDown={onKeyDown}
        />
        <button type="submit" className="classes-btn-primary" disabled={!text.trim() || !day}>남기기</button>
      </form>
      {restricted.length > 0 && (
        <p className="class-memo-warn">{restricted.join('·')} 내용은 생기부 문장에 쓸 수 없어요. 메모로만 남겨 두세요.</p>
      )}
      {saved.length > 0 && (
        <ul className="class-memo-saved" data-testid="class-memo-saved" aria-live="polite">
          {saved.map(item => (
            <li key={item.id}>
              <span aria-hidden="true">✓</span>
              <span className="class-memo-saved-text">{item.text}</span>
              <span className="class-memo-saved-day">{formatShortDay(item.date)}</span>
              <button type="button" className="class-memo-undo" aria-label={`되돌리기: ${item.text}`} onClick={() => undo([item])}>되돌리기</button>
            </li>
          ))}
        </ul>
      )}
    </ClassSheet>
  );
};

export default MemoSheet;
